import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import theme from '../UserDashboard/theme';
import { fetchRobotHistoryApp, fetchRobotHistoryAppDetail } from '../services/userRobotApi';

const PAGE_SIZE = 20;

function formatDateTime(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Number(seconds);
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function formatId(value) {
  if (value == null || value === '') return '—';
  const s = String(value);
  return s.length > 24 ? `${s.slice(0, 24)}…` : s;
}

function formatNumber(value, suffix = '') {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value)}${suffix}`;
}

/** Normalize result_map_image for <img src> (data URL, http URL, or raw base64). */
function toMapImageSrc(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (!s) return null;
  if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) return s;
  return `data:image/png;base64,${s}`;
}

function ResultMapImage({ imageValue }) {
  const src = toMapImageSrc(imageValue);
  if (!src) {
    return (
      <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary }}>
        No result map image for this session.
      </Typography>
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt="Disinfection result map"
      sx={{
        display: 'block',
        maxWidth: '100%',
        width: 'auto',
        height: 'auto',
        maxHeight: 480,
        borderRadius: 2,
        border: `1px solid ${theme.colors.borderDefault}`,
        bgcolor: theme.colors.bgTertiary,
      }}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
}

function DetailItem({ label, value }) {
  return (
    <Box sx={{ py: 0.75 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: theme.colors.textPrimary, wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function ArraySection({ title, emptyText, children }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.colors.textPrimary, mb: 1 }}>
        {title}
      </Typography>
      {children || (
        <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>{emptyText}</Typography>
      )}
    </Box>
  );
}

function HistorySessionDetails({ row }) {
  const uvLightTimes = Array.isArray(row.uvLightTimes) ? row.uvLightTimes : [];
  const motionEvents = Array.isArray(row.motionDetectionTimes) ? row.motionDetectionTimes : [];
  const objects = Array.isArray(row.objectDetection) ? row.objectDetection : [];

  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.colors.textSecondary, mb: 1 }}>
        Session & robot
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Robot ID" value={row.robotId} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Email" value={row.emailId} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Model" value={row.model} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="User ID" value={formatId(row.userId)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Room ID" value={formatId(row.roomId)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Room name" value={row.roomName} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Date" value={formatDateTime(row.date)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Mode" value={row.mode} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Status" value={row.status} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.colors.textSecondary, mb: 1 }}>
        Disinfection timing
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Start time" value={formatDateTime(row.disinfectionStartTime)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="End time" value={formatDateTime(row.disinfectionEndTime)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Duration" value={formatDuration(row.disinfectionTimeTakenSeconds)} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.colors.textSecondary, mb: 1 }}>
        Usage & location
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Battery used" value={formatNumber(row.batteryUsageInPercentage, '%')} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="UVC light usage" value={formatDuration(row.uvLightUsageInSeconds)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Motor runtime" value={formatDuration(row.motorRuntimeInSeconds)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem
            label="Distance travelled"
            value={formatNumber(row.distanceTravelledInMeters, ' m')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Sub-location" value={row.subLocation} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Map name" value={row.map_name} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Total objects" value={row.total_object} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DetailItem label="Completed objects" value={row.completed_object} />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.colors.textPrimary, mb: 1 }}>
          Result map image
        </Typography>
        <ResultMapImage imageValue={row.result_map_image} />
      </Box>

      <ArraySection title="UVC light periods" emptyText="No UVC light periods recorded.">
        {uvLightTimes.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Start</TableCell>
                  <TableCell sx={thSx}>End</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uvLightTimes.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell sx={tdSx}>{formatDateTime(entry.startTime)}</TableCell>
                    <TableCell sx={tdSx}>{formatDateTime(entry.endTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ArraySection>

      <ArraySection title="Motion detection events" emptyText="No motion detection events.">
        {motionEvents.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Detected</TableCell>
                  <TableCell sx={thSx}>Resume</TableCell>
                  <TableCell sx={thSx}>Aborted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {motionEvents.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell sx={tdSx}>{formatDateTime(entry.detectedTime)}</TableCell>
                    <TableCell sx={tdSx}>{formatDateTime(entry.resumeTime)}</TableCell>
                    <TableCell sx={tdSx}>{formatDateTime(entry.abortedTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ArraySection>

      <ArraySection title="Object detection" emptyText="No object detection data.">
        {objects.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Object</TableCell>
                  <TableCell sx={thSx}>X</TableCell>
                  <TableCell sx={thSx}>Y</TableCell>
                  <TableCell sx={thSx}>Angle</TableCell>
                  <TableCell sx={thSx} align="right">
                    Distance
                  </TableCell>
                  <TableCell sx={thSx} align="right">
                    Accuracy %
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {objects.map((obj, i) => (
                  <TableRow key={i}>
                    <TableCell sx={tdSx}>{obj.objectName || '—'}</TableCell>
                    <TableCell sx={tdSx}>{obj.objectCoordinate?.x ?? '—'}</TableCell>
                    <TableCell sx={tdSx}>{obj.objectCoordinate?.y ?? '—'}</TableCell>
                    <TableCell sx={tdSx}>{obj.objectCoordinate?.angle ?? '—'}</TableCell>
                    <TableCell sx={tdSx} align="right">
                      {obj.distance ?? '—'}
                    </TableCell>
                    <TableCell sx={tdSx} align="right">
                      {obj.accuracyPercentage ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ArraySection>

      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DetailItem label="Created at" value={formatDateTime(row.createdAt)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailItem label="Updated at" value={formatDateTime(row.updatedAt)} />
        </Grid>
        <Grid item xs={12}>
          <DetailItem label="Record ID" value={row._id} />
        </Grid>
      </Grid>
    </Box>
  );
}

function HistorySessionRow({ row, onOpen }) {
  const isCompleted = String(row.status || '').toLowerCase() === 'completed';
  const room =
    row.roomName != null && String(row.roomName).trim() ? String(row.roomName).trim() : '—';
  const startLabel = formatDateTime(row.disinfectionStartTime || row.date);

  return (
    <Paper
      elevation={0}
      onClick={() => onOpen(row)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(row);
        }
      }}
      sx={{
        p: 2,
        mb: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        borderRadius: 2,
        border: `1px solid ${theme.colors.borderDefault}`,
        bgcolor: theme.colors.bgPrimary,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          borderColor: theme.colors.brandPrimary,
          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.1)',
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.colors.textPrimary }}>
          {room}
        </Typography>
        <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.25 }}>
          {startLabel} · {formatDuration(row.disinfectionTimeTakenSeconds)} · {row.mode || '—'}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={row.status || '—'}
        color={isCompleted ? 'success' : 'warning'}
        variant="outlined"
        icon={isCompleted ? <CheckCircleOutlinedIcon /> : <WarningAmberOutlinedIcon />}
        sx={{ fontSize: '0.75rem', height: 24, flexShrink: 0 }}
      />
      <ChevronRightIcon sx={{ color: theme.colors.textSecondary, flexShrink: 0 }} />
    </Paper>
  );
}

function HistoryDetailDialog({ open, onClose, row, loading, error }) {
  const room =
    row?.roomName != null && String(row.roomName).trim() ? String(row.roomName).trim() : 'Session';
  const titleTime = formatDateTime(row?.disinfectionStartTime || row?.date);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: theme.colors.textPrimary }}>
            {room}
          </Typography>
          <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mt: 0.5 }}>
            {titleTime}
            {row?.status ? ` · ${row.status}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" size="small" sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: theme.colors.bgSecondary }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
          </Box>
        ) : error ? (
          <Typography sx={{ fontSize: 14, color: theme.colors.accentDanger }}>{error}</Typography>
        ) : row ? (
          <HistorySessionDetails row={row} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

const thSx = { fontWeight: 700, fontSize: 11, color: theme.colors.textSecondary, py: 1 };
const tdSx = { fontSize: 12, py: 1 };

/** Per-robot disinfection history from GET /robot/history/app */
export function RobotHistoryView({ robotId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalData: 0,
    totalPages: 0,
    limit: PAGE_SIZE,
  });
  const [amps, setAmps] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const loadHistory = useCallback(async () => {
    if (!robotId) {
      setError('Robot ID is required.');
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetchRobotHistoryApp(robotId, { page, limit: PAGE_SIZE });
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(raw);
      setAmps(res.data?.amps ?? null);
      const p = res.data?.pagination || {};
      setPagination({
        totalData: p.totalData ?? raw.length,
        totalPages: p.totalPages ?? 1,
        limit: p.limit ?? PAGE_SIZE,
      });
    } catch (err) {
      setRows([]);
      setAmps(null);
      setPagination({ totalData: 0, totalPages: 0, limit: PAGE_SIZE });
      setError(err.response?.data?.message || 'Could not load disinfection history.');
    } finally {
      setLoading(false);
    }
  }, [robotId, page]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const summary = useMemo(() => {
    const completed = rows.filter((r) => String(r.status || '').toLowerCase() === 'completed').length;
    const partial = rows.filter((r) => String(r.status || '').toLowerCase() === 'partial').length;
    return { completed, partial };
  }, [rows]);

  const handlePageChange = (_e, value) => {
    setPage(value);
  };

  const handleOpenSession = useCallback(
    async (row) => {
      const historyId = row?._id;
      if (!historyId) return;

      setDialogOpen(true);
      setDetailRow(row);
      setDetailLoading(true);
      setDetailError('');

      try {
        const res = await fetchRobotHistoryAppDetail(historyId, robotId);
        const full = res.data?.data;
        if (full && typeof full === 'object') {
          setDetailRow(full);
        }
      } catch (err) {
        setDetailError(
          err.response?.data?.message || 'Could not load session details including map image.',
        );
      } finally {
        setDetailLoading(false);
      }
    },
    [robotId],
  );

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDetailRow(null);
    setDetailError('');
    setDetailLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography sx={{ fontSize: 14, color: theme.colors.accentDanger }}>{error}</Typography>
    );
  }

  if (pagination.totalData === 0 && rows.length === 0) {
    return (
      <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
        No disinfection history found for this robot.
      </Typography>
    );
  }

  return (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${theme.colors.borderDefault}`,
              bgcolor: theme.colors.bgSecondary,
            }}
          >
            <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary, fontWeight: 600 }}>
              Total sessions
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: theme.colors.textPrimary }}>
              {pagination.totalData}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${theme.colors.borderDefault}`,
              bgcolor: theme.colors.bgSecondary,
            }}
          >
            <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary, fontWeight: 600 }}>
              On this page
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: theme.colors.textPrimary }}>
              {rows.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${theme.colors.borderDefault}`,
              bgcolor: theme.colors.bgSecondary,
            }}
          >
            <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary, fontWeight: 600 }}>
              Completed (page)
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: theme.colors.textPrimary }}>
              {summary.completed}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${theme.colors.borderDefault}`,
              bgcolor: theme.colors.bgSecondary,
            }}
          >
            <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary, fontWeight: 600 }}>
              Partial (page)
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: theme.colors.textPrimary }}>
              {summary.partial}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {amps != null && (
        <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mb: 1.5 }}>
          Robot amps: {amps}
        </Typography>
      )}

      <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 1.5 }}>
        Click a session to open full details and result map image. Page {page} of{' '}
        {pagination.totalPages || 1}.
      </Typography>

      <Box sx={{ maxHeight: 'calc(100vh - 420px)', overflow: 'auto', pr: 0.5 }}>
        {rows.map((row, index) => (
          <HistorySessionRow
            key={row._id || `hist-${page}-${index}`}
            row={row}
            onOpen={handleOpenSession}
          />
        ))}
      </Box>

      <HistoryDetailDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        row={detailRow}
        loading={detailLoading}
        error={detailError}
      />

      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="medium"
          />
        </Box>
      )}
    </Box>
  );
}

export default RobotHistoryView;
